import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '../App';
import Header from '../components/Header';
import { SEO } from '../components/SEO';
import { Sparkles, Pin, ChevronUp, ArrowRight } from 'lucide-react';

const COLUMNS = [
  { key: 'planned', label: 'Planned', sub: 'On the build list, soon.', tone: 'border-amber-200 bg-amber-50/40' },
  { key: 'in_development', label: 'In development', sub: 'Being built right now.', tone: 'border-blue-200 bg-blue-50/40' },
  { key: 'released', label: 'Released', sub: 'Shipped, in your hands.', tone: 'border-emerald-200 bg-emerald-50/40' },
];

/**
 * Public-ish roadmap page (logged-in only, no guests).
 *
 * Shows three columns: Planned, In development, Released. Both pools
 * are merged so providers see what clients have asked for and vice
 * versa, with role pills on each card. Under-review requests stay
 * on the pool board, not here.
 */
export default function Roadmap() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ planned: [], in_development: [], released: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Wait for auth to resolve before deciding to redirect.
    if (authLoading) return;
    if (!user) {
      navigate('/login?next=/roadmap');
      return;
    }
    api.get('/feature-requests/roadmap')
      .then(res => setData(res.data || {}))
      .catch(() => setData({ planned: [], in_development: [], released: [] }))
      .finally(() => setLoading(false));
  }, [authLoading, user, navigate]);

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <SEO title="Roadmap, Zuriya" description="What we&rsquo;re building next, based on your feedback." />
      <Header />
      <main className="max-w-6xl mx-auto px-4 py-10">
        <div className="mb-8">
          <p className="text-xs text-stone-500 uppercase tracking-widest mb-2 flex items-center gap-2">
            <Sparkles className="h-3 w-3 text-[#D4AF37]" />Roadmap
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl text-[#1A1A1A]">
            What we&rsquo;re building next
          </h1>
          <p className="text-sm text-stone-500 mt-2 max-w-2xl">
            Every card here started as a feature request from a client or a provider. The board updates as
            requests move through review, planning, and into the build queue.{' '}
            <button
              type="button"
              onClick={() => navigate(user?.role === 'provider' ? '/provider/dashboard?tab=feedback' : '/client/dashboard?tab=feedback')}
              className="text-[#D4AF37] underline"
              data-testid="roadmap-cta-submit"
            >
              Submit a request <ArrowRight className="h-3 w-3 inline" />
            </button>
          </p>
        </div>

        {loading ? (
          <p className="text-sm text-stone-400">Loading&hellip;</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="roadmap-columns">
            {COLUMNS.map(col => {
              const items = data[col.key] || [];
              return (
                <section
                  key={col.key}
                  className={`rounded-lg border ${col.tone} p-4`}
                  data-testid={`roadmap-column-${col.key}`}
                >
                  <div className="mb-3">
                    <p className="font-heading text-base font-medium text-[#1A1A1A]">{col.label}</p>
                    <p className="text-xs text-stone-500">{col.sub}</p>
                  </div>
                  {items.length === 0 ? (
                    <p className="text-xs text-stone-400 italic mt-6 text-center">Nothing here yet.</p>
                  ) : (
                    <ul className="space-y-2" data-testid={`roadmap-list-${col.key}`}>
                      {items.map(item => (
                        <li
                          key={item.request_id}
                          className="bg-white rounded-lg border border-[#E6E4DD] p-3"
                          data-testid={`roadmap-card-${item.request_id}`}
                        >
                          <div className="flex items-start gap-2">
                            {item.pinned && <Pin className="h-3 w-3 text-[#D4AF37] mt-1" />}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#1A1A1A]">{item.title}</p>
                              {item.description && (
                                <p className="text-xs text-stone-600 mt-1 line-clamp-3">{item.description}</p>
                              )}
                              <div className="flex items-center gap-2 mt-2 text-[10px] text-stone-500">
                                <span className="capitalize bg-[#F9F6F0] border border-[#E6E4DD] px-1.5 py-0.5 rounded-full">
                                  {item.author_role}
                                </span>
                                <span className="inline-flex items-center gap-0.5">
                                  <ChevronUp className="h-3 w-3" />{item.upvote_count}
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
